import React, { useState, useEffect } from 'react';
import { 
    Building2, Users, Calendar, DollarSign, TrendingUp, FileText, 
    Mail, Phone, Globe, MapPin, Plus, Search, Filter, ChevronRight,
    Star, CheckCircle, AlertCircle, Clock, Edit, Eye, Trash2, Briefcase,
    Send, X, BarChart3, User
} from 'lucide-react';
import CustomerCreateForm from '../components/CustomerCreateForm';

const CustomerManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        industry: '',
        company_size: '',
        service_level: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    
    // Portfolio modal state
    const [activePortfolioTab, setActivePortfolioTab] = useState('overview');
    const [portfolioJobs, setPortfolioJobs] = useState([]);
    const [portfolioSubmissions, setPortfolioSubmissions] = useState([]);
    const [loadingPortfolio, setLoadingPortfolio] = useState(false);

    // Fetch customers from API
    useEffect(() => {
        fetchCustomers();
    }, [filters]);

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
            
            // Build query parameters
            const queryParams = new URLSearchParams({
                limit: 50,
                offset: 0,
                ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
            });

            const response = await fetch(`${backendUrl}/api/customers?${queryParams}`);
            const data = await response.json();
            
            if (data.success) {
                setCustomers(data.customers || []);
            } else {
                console.error('Error fetching customers:', data.error);
                // Load mock data for demo
                setCustomers(getMockCustomers());
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            // Load mock data for demo
            setCustomers(getMockCustomers());
        } finally {
            setLoading(false);
        }
    };

    // Handle customer creation success
    const handleCustomerCreated = (message) => {
        setSuccessMessage(message);
        fetchCustomers(); // Refresh the customer list
        
        // Clear success message after 5 seconds
        setTimeout(() => {
            setSuccessMessage('');
        }, 5000);
    };

    // Handle customer edit
    const handleEditCustomer = (customer) => {
        setEditingCustomer(customer);
        setShowCreateModal(true);
    };

    // Handle customer updated
    const handleCustomerUpdated = (message) => {
        setSuccessMessage(message);
        setEditingCustomer(null);
        fetchCustomers(); // Refresh the customer list
        
        // Clear success message after 5 seconds
        setTimeout(() => {
            setSuccessMessage('');
        }, 5000);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditingCustomer(null);
    };
    
    // Fetch customer portfolio data
    const fetchCustomerPortfolio = async (customerId) => {
        try {
            setLoadingPortfolio(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
            
            // Fetch jobs for this customer
            const jobsResponse = await fetch(`${backendUrl}/api/jobs?customer_id=${customerId}`);
            const jobsData = await jobsResponse.json();
            if (jobsData.success) {
                setPortfolioJobs(jobsData.jobs || []);
            }
            
            // Fetch submissions for this customer's jobs
            const submissionsResponse = await fetch(`${backendUrl}/api/submissions?customer_id=${customerId}`);
            const submissionsData = await submissionsResponse.json();
            if (submissionsData.success) {
                setPortfolioSubmissions(submissionsData.submissions || []);
            }
        } catch (error) {
            console.error('Error fetching customer portfolio:', error);
        } finally {
            setLoadingPortfolio(false);
        }
    };
    
    // Handle customer selection for portfolio view
    const handleViewCustomerPortfolio = (customer) => {
        setSelectedCustomer(customer);
        setActivePortfolioTab('overview');
        fetchCustomerPortfolio(customer.id);
    };
    
    // Handle closing portfolio modal
    const handleClosePortfolio = () => {
        setSelectedCustomer(null);
        setPortfolioJobs([]);
        setPortfolioSubmissions([]);
        setActivePortfolioTab('overview');
    };

    // Mock data for demo purposes
    const getMockCustomers = () => ([
        {
            id: '1',
            company_name: 'TechCorp Inc',
            industry: 'Technology',
            company_size: '500-1000',
            service_level: 'enterprise',
            status: 'active',
            primary_contact_name: 'Sarah Johnson',
            primary_contact_email: 'sarah.johnson@techcorp.com',
            primary_contact_title: 'Head of Talent Acquisition',
            total_open_requisitions: 8,
            total_job_requisitions: 25,
            total_hires: 12,
            satisfaction_rating: 4.8,
            vendor_portal_url: 'techcorp.portal.junotaling.com',
            created_at: '2024-01-15T10:30:00Z'
        },
        {
            id: '2',
            company_name: 'Global Finance LLC',
            industry: 'Financial Services',
            company_size: '1000+',
            service_level: 'premium',
            status: 'active',
            primary_contact_name: 'Michael Chen',
            primary_contact_email: 'michael.chen@globalfinance.com',
            primary_contact_title: 'VP of Human Resources',
            total_open_requisitions: 5,
            total_job_requisitions: 18,
            total_hires: 8,
            satisfaction_rating: 4.5,
            vendor_portal_url: 'global-finance-llc.portal.junotaling.com',
            created_at: '2024-02-20T14:15:00Z'
        },
        {
            id: '3',
            company_name: 'Healthcare Plus',
            industry: 'Healthcare',
            company_size: '100-500',
            service_level: 'standard',
            status: 'active',
            primary_contact_name: 'Dr. Emily Rodriguez',
            primary_contact_email: 'emily.rodriguez@healthcareplus.com',
            primary_contact_title: 'Chief People Officer',
            total_open_requisitions: 12,
            total_job_requisitions: 35,
            total_hires: 20,
            satisfaction_rating: 4.9,
            vendor_portal_url: 'healthcare-plus.portal.junotaling.com',
            created_at: '2023-12-08T09:45:00Z'
        }
    ]);

    // Filter customers based on search term
    const filteredCustomers = customers.filter(customer => {
        const companyName = customer.company_name || customer.client_name || '';
        const contactName = customer.primary_contact_name || '';
        const industry = customer.industry || '';
        
        return companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               industry.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getServiceLevelBadge = (level) => {
        const configs = {
            basic: { color: 'bg-gray-100 text-gray-800', icon: '⭐' },
            standard: { color: 'bg-blue-100 text-blue-800', icon: '⭐⭐' },
            premium: { color: 'bg-purple-100 text-purple-800', icon: '⭐⭐⭐' },
            enterprise: { color: 'bg-yellow-100 text-yellow-800', icon: '👑' }
        };
        const config = configs[level] || configs.standard;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.icon} {level.charAt(0).toUpperCase() + level.slice(1)}
            </span>
        );
    };

    const getStatusBadge = (status) => {
        const configs = {
            active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            inactive: { color: 'bg-gray-100 text-gray-800', icon: Clock },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle }
        };
        const config = configs[status] || configs.active;
        const IconComponent = config.icon;
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <IconComponent className="h-3 w-3 mr-1" />
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const getSatisfactionStars = (rating) => {
        const fullStars = Math.floor(rating || 0);
        const hasHalfStar = (rating || 0) % 1 !== 0;
        
        return (
            <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                    <Star 
                        key={i} 
                        className={`h-4 w-4 ${
                            i < fullStars 
                                ? 'text-yellow-400 fill-current' 
                                : i === fullStars && hasHalfStar 
                                    ? 'text-yellow-400 fill-current opacity-50' 
                                    : 'text-gray-300'
                        }`} 
                    />
                ))}
                <span className="ml-1 text-sm text-gray-600">{rating?.toFixed(1) || 'N/A'}</span>
            </div>
        );
    };

    const CustomerStats = ({ customer }) => (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center">
                    <FileText className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                        <p className="text-xs text-gray-600">Open Requests</p>
                        <p className="text-lg font-semibold text-blue-600">{customer.total_open_requisitions || 0}</p>
                    </div>
                </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                        <p className="text-xs text-gray-600">Job Requisitions</p>
                        <p className="text-lg font-semibold text-green-600">{customer.total_job_requisitions || 0}</p>
                    </div>
                </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
                <div className="flex items-center">
                    <Users className="h-5 w-5 text-purple-600 mr-2" />
                    <div>
                        <p className="text-xs text-gray-600">Total Hires</p>
                        <p className="text-lg font-semibold text-purple-600">{customer.total_hires || 0}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Success Message Toast */}
            {successMessage && (
                <div className="fixed top-4 right-4 z-50 animate-fade-in">
                    <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">{successMessage}</span>
                        <button 
                            onClick={() => setSuccessMessage('')}
                            className="ml-2 hover:text-gray-200"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                                <Building2 className="h-8 w-8 text-blue-600 mr-3" />
                                Customer Management
                            </h1>
                            <p className="mt-2 text-gray-600">Manage your recruiting clients and vendor relationships</p>
                        </div>
                        <div className="mt-4 md:mt-0">
                            <button 
                                onClick={() => setShowCreateModal(true)}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                Add New Customer
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white p-4 rounded-lg shadow-sm border mb-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search customers, contacts, or industries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Filters */}
                        <div className="flex space-x-3">
                            <select 
                                value={filters.status} 
                                onChange={(e) => setFilters({...filters, status: e.target.value})}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                            </select>

                            <input
                                type="text"
                                placeholder="Industry..."
                                value={filters.industry}
                                onChange={(e) => setFilters({...filters, industry: e.target.value})}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Customer Table */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading customers...</p>
                        </div>
                    ) : filteredCustomers.length === 0 ? (
                        <div className="p-8 text-center">
                            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No customers found matching your criteria.</p>
                            <button 
                                onClick={() => setShowCreateModal(true)}
                                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Your First Customer
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full table-fixed divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Company Name
                                        </th>
                                        <th className="w-[8%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="w-[10%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Industry
                                        </th>
                                        <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Contact Person
                                        </th>
                                        <th className="w-[10%] px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Open Requests
                                        </th>
                                        <th className="w-[8%] px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Jobs
                                        </th>
                                        <th className="w-[8%] px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Hires
                                        </th>
                                        <th className="w-[10%] px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredCustomers.map((customer) => (
                                        <tr 
                                            key={customer.id}
                                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                                            onClick={() => handleViewCustomerPortfolio(customer)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-full">
                                                        <Building2 className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                    <div className="ml-4 truncate">
                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                            {customer.company_name || customer.client_name || 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(customer.status)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="truncate">{customer.industry || 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 truncate">{customer.primary_contact_name || 'N/A'}</div>
                                                <div className="text-sm text-gray-500 truncate">{customer.primary_contact_email || 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-blue-600">
                                                {customer.total_open_requisitions || 0}
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-green-600">
                                                {customer.total_job_requisitions || 0}
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-medium text-purple-600">
                                                {customer.total_hires || 0}
                                            </td>
                                            <td className="px-6 py-4 text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewCustomerPortfolio(customer);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="View Details"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditCustomer(customer);
                                                        }}
                                                        className="text-gray-600 hover:text-gray-900"
                                                        title="Edit Customer"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Customer Modal */}
            {showCreateModal && (
                <CustomerCreateForm
                    customer={editingCustomer}
                    onClose={handleCloseModal}
                    onSuccess={editingCustomer ? handleCustomerUpdated : handleCustomerCreated}
                />
            )}

            {/* Company Portfolio Modal */}
            {selectedCustomer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center">
                                <Building2 className="h-6 w-6 text-blue-600 mr-3" />
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {selectedCustomer.company_name || selectedCustomer.client_name || 'N/A'}
                                    </h2>
                                    <p className="text-sm text-gray-500">Company Portfolio</p>
                                </div>
                            </div>
                            <button 
                                onClick={handleClosePortfolio}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        
                        {/* Tab Navigation */}
                        <div className="border-b border-gray-200 px-6">
                            <div className="flex space-x-8">
                                <button
                                    onClick={() => setActivePortfolioTab('overview')}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                        activePortfolioTab === 'overview'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Overview
                                </button>
                                <button
                                    onClick={() => setActivePortfolioTab('jobs')}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                        activePortfolioTab === 'jobs'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Active Job Openings ({portfolioJobs.filter(j => j.status === 'open').length})
                                </button>
                                <button
                                    onClick={() => setActivePortfolioTab('submissions')}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                        activePortfolioTab === 'submissions'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Submissions ({portfolioSubmissions.length})
                                </button>
                                <button
                                    onClick={() => setActivePortfolioTab('analytics')}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                        activePortfolioTab === 'analytics'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Performance Analytics
                                </button>
                                <button
                                    onClick={() => setActivePortfolioTab('statistics')}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                        activePortfolioTab === 'statistics'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Statistics
                                </button>
                            </div>
                        </div>
                        
                        {/* Tab Content */}
                        <div className="p-6">
                            {loadingPortfolio ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Loading portfolio data...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Overview Tab */}
                                    {activePortfolioTab === 'overview' && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <h3 className="text-lg font-medium mb-4 flex items-center">
                                                        <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                                                        Company Information
                                                    </h3>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="text-sm text-gray-500">Industry</label>
                                                            <p className="font-medium">{selectedCustomer.industry || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-gray-500">Company Size</label>
                                                            <p className="font-medium">{selectedCustomer.company_size || 'N/A'} employees</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-gray-500">Status</label>
                                                            <div className="mt-1">{getStatusBadge(selectedCustomer.status)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <h3 className="text-lg font-medium mb-4 flex items-center">
                                                        <Mail className="h-5 w-5 mr-2 text-blue-600" />
                                                        Primary Contact
                                                    </h3>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="text-sm text-gray-500">Name & Title</label>
                                                            <p className="font-medium">{selectedCustomer.primary_contact_name || 'N/A'}</p>
                                                            <p className="text-sm text-gray-600">{selectedCustomer.primary_contact_title || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-gray-500">Email</label>
                                                            <p className="font-medium text-blue-600">{selectedCustomer.primary_contact_email || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-gray-500">Vendor Portal</label>
                                                            <a 
                                                                href={`https://${selectedCustomer.vendor_portal_url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-medium text-blue-600 hover:underline"
                                                            >
                                                                {selectedCustomer.vendor_portal_url || 'N/A'}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h3 className="text-lg font-medium mb-4 flex items-center">
                                                    <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                                                    Performance Metrics
                                                </h3>
                                                <CustomerStats customer={selectedCustomer} />
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Active Job Openings Tab */}
                                    {activePortfolioTab === 'jobs' && (
                                        <div>
                                            <h3 className="text-lg font-medium mb-4">Active Job Openings</h3>
                                            {portfolioJobs.filter(j => j.status === 'open').length === 0 ? (
                                                <div className="text-center py-8">
                                                    <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                                    <p className="text-gray-600">No active job openings for this customer.</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {portfolioJobs.filter(j => j.status === 'open').map((job) => (
                                                        <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <h4 className="font-medium text-gray-900">{job.job_title || job.title || 'N/A'}</h4>
                                                                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                                                                        <span className="flex items-center">
                                                                            <MapPin className="h-4 w-4 mr-1" />
                                                                            {job.location || 'N/A'}
                                                                        </span>
                                                                        <span className="flex items-center">
                                                                            <DollarSign className="h-4 w-4 mr-1" />
                                                                            {job.salary_range || 'N/A'}
                                                                        </span>
                                                                        <span className="flex items-center">
                                                                            <Calendar className="h-4 w-4 mr-1" />
                                                                            {job.employment_type || 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                                    job.status === 'open' 
                                                                        ? 'bg-green-100 text-green-800' 
                                                                        : 'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                    {job.status || 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Submissions Tab */}
                                    {activePortfolioTab === 'submissions' && (
                                        <div>
                                            <h3 className="text-lg font-medium mb-4">Candidate Submissions</h3>
                                            {portfolioSubmissions.length === 0 ? (
                                                <div className="text-center py-8">
                                                    <Send className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                                    <p className="text-gray-600">No candidate submissions for this customer.</p>
                                                </div>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {portfolioSubmissions.map((submission) => (
                                                                <tr key={submission.id} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3 text-sm text-gray-900">{submission.candidate_name || 'N/A'}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-900">{submission.job_title || 'N/A'}</td>
                                                                    <td className="px-4 py-3 text-sm">
                                                                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                                                            {(submission.current_status || submission.status) || 'N/A'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600">{submission.submitted_by_name || 'N/A'}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600">
                                                                        {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : 'N/A'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Performance Analytics Tab */}
                                    {activePortfolioTab === 'analytics' && (
                                        <div className="space-y-6">
                                            <div className="flex items-center justify-between mb-6">
                                                <h3 className="text-lg font-medium flex items-center gap-2">
                                                    <BarChart3 className="h-5 w-5 text-blue-600" />
                                                    Customer Performance Analytics
                                                </h3>
                                            </div>

                                            {/* Time Period Metrics */}
                                            <div className="space-y-4">
                                                {[
                                                    { label: 'Last Week', days: 7 },
                                                    { label: 'Last Month', days: 30 },
                                                    { label: 'Last Quarter', days: 90 },
                                                    { label: 'Last Year', days: 365 }
                                                ].map(period => {
                                                    const cutoffDate = new Date();
                                                    cutoffDate.setDate(cutoffDate.getDate() - period.days);
                                                    
                                                    const periodJobs = portfolioJobs.filter(j => 
                                                        new Date(j.created_at) >= cutoffDate
                                                    );
                                                    const periodSubmissions = portfolioSubmissions.filter(s => 
                                                        new Date(s.submitted_at) >= cutoffDate
                                                    );
                                                    const interviews = periodSubmissions.filter(s => 
                                                        s.status === 'interview_scheduled'
                                                    );
                                                    const offers = periodSubmissions.filter(s => 
                                                        s.status === 'offer_made' || s.status === 'offer_extended'
                                                    );
                                                    const onboardings = periodSubmissions.filter(s => 
                                                        s.status === 'offer_accepted' || s.status === 'onboarding'
                                                    );

                                                    return (
                                                        <div key={period.label} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                            <h4 className="font-semibold text-gray-900 mb-3">{period.label}</h4>
                                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                                <div className="bg-white p-3 rounded-lg border border-blue-200">
                                                                    <p className="text-xs text-gray-600 mb-1">Job Openings</p>
                                                                    <p className="text-2xl font-bold text-blue-600">{periodJobs.length}</p>
                                                                </div>
                                                                <div className="bg-white p-3 rounded-lg border border-purple-200">
                                                                    <p className="text-xs text-gray-600 mb-1">Submissions</p>
                                                                    <p className="text-2xl font-bold text-purple-600">{periodSubmissions.length}</p>
                                                                </div>
                                                                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                                                                    <p className="text-xs text-gray-600 mb-1">Interviews</p>
                                                                    <p className="text-2xl font-bold text-indigo-600">{interviews.length}</p>
                                                                </div>
                                                                <div className="bg-white p-3 rounded-lg border border-green-200">
                                                                    <p className="text-xs text-gray-600 mb-1">Offers</p>
                                                                    <p className="text-2xl font-bold text-green-600">{offers.length}</p>
                                                                </div>
                                                                <div className="bg-white p-3 rounded-lg border border-yellow-200">
                                                                    <p className="text-xs text-gray-600 mb-1">Onboardings</p>
                                                                    <p className="text-2xl font-bold text-yellow-600">{onboardings.length}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Sales Managers with Open Jobs */}
                                            <div className="border border-gray-200 rounded-lg p-4 bg-white">
                                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                    <Users className="h-5 w-5 text-orange-600" />
                                                    Sales Managers with Open Jobs
                                                </h4>
                                                {(() => {
                                                    const openJobs = portfolioJobs.filter(j => j.status === 'open');
                                                    const salesManagers = {};
                                                    
                                                    openJobs.forEach(job => {
                                                        const manager = job.account_manager || job.created_by_name || 'Unassigned';
                                                        if (!salesManagers[manager]) {
                                                            salesManagers[manager] = [];
                                                        }
                                                        salesManagers[manager].push(job);
                                                    });

                                                    return Object.keys(salesManagers).length > 0 ? (
                                                        <div className="space-y-3">
                                                            {Object.entries(salesManagers).map(([manager, jobs]) => (
                                                                <div key={manager} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                                                                            <User className="h-5 w-5 text-orange-600" />
                                                                        </div>
                                                                        <div>
                                                                            <p className="font-medium text-gray-900">{manager}</p>
                                                                            <p className="text-sm text-gray-600">Managing {jobs.length} open {jobs.length === 1 ? 'job' : 'jobs'}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="px-3 py-1 bg-orange-600 text-white rounded-full text-sm font-semibold">
                                                                            {jobs.length}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-center text-gray-500 py-4">No open jobs currently</p>
                                                    );
                                                })()}
                                            </div>

                                            {/* Mean Time to Close */}
                                            <div className="border border-gray-200 rounded-lg p-4 bg-white">
                                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                    <Clock className="h-5 w-5 text-teal-600" />
                                                    Mean Time to Close (Job Opening → Offer Made)
                                                </h4>
                                                {(() => {
                                                    const jobsWithOffers = portfolioJobs.filter(job => {
                                                        const jobSubmissions = portfolioSubmissions.filter(s => s.job_id === job.id);
                                                        return jobSubmissions.some(s => s.status === 'offer_made' || s.status === 'offer_extended');
                                                    });

                                                    if (jobsWithOffers.length === 0) {
                                                        return <p className="text-center text-gray-500 py-4">No completed offers yet</p>;
                                                    }

                                                    let totalDays = 0;
                                                    const validJobs = [];

                                                    jobsWithOffers.forEach(job => {
                                                        const jobSubmissions = portfolioSubmissions.filter(s => s.job_id === job.id);
                                                        const offerSubmission = jobSubmissions.find(s => 
                                                            s.status === 'offer_made' || s.status === 'offer_extended'
                                                        );
                                                        
                                                        if (job.created_at && offerSubmission?.submitted_at) {
                                                            const jobDate = new Date(job.created_at);
                                                            const offerDate = new Date(offerSubmission.submitted_at);
                                                            const days = Math.floor((offerDate - jobDate) / (1000 * 60 * 60 * 24));
                                                            if (days >= 0) {
                                                                totalDays += days;
                                                                validJobs.push({ job, days });
                                                            }
                                                        }
                                                    });

                                                    const meanDays = validJobs.length > 0 ? Math.round(totalDays / validJobs.length) : 0;

                                                    return (
                                                        <div className="space-y-4">
                                                            <div className="bg-teal-50 p-6 rounded-lg text-center">
                                                                <p className="text-sm text-gray-600 mb-2">Average Time to Close</p>
                                                                <p className="text-4xl font-bold text-teal-600">{meanDays}</p>
                                                                <p className="text-sm text-gray-600 mt-1">days</p>
                                                                <p className="text-xs text-gray-500 mt-2">Based on {validJobs.length} {validJobs.length === 1 ? 'job' : 'jobs'} with offers</p>
                                                            </div>
                                                            
                                                            {/* Recent Closed Jobs */}
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-700 mb-2">Recent Closures:</p>
                                                                <div className="space-y-2">
                                                                    {validJobs.slice(0, 5).map(({ job, days }) => (
                                                                        <div key={job.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                                            <span className="text-sm text-gray-900 truncate flex-1">{job.title || job.job_title}</span>
                                                                            <span className={`text-sm font-semibold ml-2 ${
                                                                                days <= 30 ? 'text-green-600' :
                                                                                days <= 60 ? 'text-yellow-600' :
                                                                                'text-red-600'
                                                                            }`}>
                                                                                {days} days
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Statistics Tab */}
                                    {activePortfolioTab === 'statistics' && (
                                        <div>
                                            <h3 className="text-lg font-medium mb-4">Performance Statistics</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <div className="bg-blue-50 p-4 rounded-lg">
                                                    <div className="flex items-center">
                                                        <FileText className="h-8 w-8 text-blue-600 mr-3" />
                                                        <div>
                                                            <p className="text-sm text-gray-600">Total Jobs</p>
                                                            <p className="text-2xl font-bold text-blue-600">{portfolioJobs.length}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-green-50 p-4 rounded-lg">
                                                    <div className="flex items-center">
                                                        <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                                                        <div>
                                                            <p className="text-sm text-gray-600">Open Jobs</p>
                                                            <p className="text-2xl font-bold text-green-600">
                                                                {portfolioJobs.filter(j => j.status === 'open').length}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-purple-50 p-4 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Send className="h-8 w-8 text-purple-600 mr-3" />
                                                        <div>
                                                            <p className="text-sm text-gray-600">Total Submissions</p>
                                                            <p className="text-2xl font-bold text-purple-600">{portfolioSubmissions.length}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-yellow-50 p-4 rounded-lg">
                                                    <div className="flex items-center">
                                                        <Users className="h-8 w-8 text-yellow-600 mr-3" />
                                                        <div>
                                                            <p className="text-sm text-gray-600">Total Hires</p>
                                                            <p className="text-2xl font-bold text-yellow-600">{selectedCustomer.total_hires || 0}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                            <button 
                                onClick={handleClosePortfolio}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    handleClosePortfolio();
                                    handleEditCustomer(selectedCustomer);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                            >
                                <Edit className="h-4 w-4" />
                                Edit Customer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerManagement;